<template>
  <div class="searchable-dropdown-container">
    <label v-if="label" :for="inputId" class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ label }}
    </label>
    <div class="relative">
      <input
        :id="inputId"
        v-model="searchValue"
        :type="inputType"
        :required="required"
        :disabled="disabled"
        :placeholder="placeholder"
        :class="inputClasses"
        @input="handleSearch"
        @focus="showDropdown = true"
        @blur="handleBlur"
        class="px-3 py-2 w-full rounded-md border border-gray-300 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

      <!-- Dropdown -->
      <div
        v-if="showDropdown && filteredItems.length > 0"
        class="overflow-y-auto absolute z-10 mt-1 w-full max-h-60 bg-white rounded-md border border-gray-300 shadow-lg dark:bg-gray-800 dark:border-gray-600">
        <div
          v-for="item in filteredItems"
          :key="getItemKey(item)"
          @click="selectItem(item)"
          class="px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-100 last:border-b-0 dark:border-gray-700 dark:hover:bg-gray-700">
          <div class="font-medium dark:text-white">{{ getItemDisplay(item) }}</div>
          <div v-if="getItemSubtext(item)" class="text-sm text-gray-600 dark:text-gray-200">
            {{ getItemSubtext(item) }}
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="absolute top-2 right-3">
        <div class="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin"></div>
      </div>

      <!-- No Results Message -->
      <div
        v-if="showDropdown && filteredItems.length === 0 && searchValue && !loading"
        class="absolute z-10 mt-1 w-full bg-white rounded-md border border-gray-300 shadow-lg dark:bg-gray-800 dark:border-gray-600">
        <div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-200">
          No {{ itemLabel }} found matching "{{ searchValue }}"
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="showDropdown && items.length === 0 && !loading && !searchValue"
        class="absolute z-10 mt-1 w-full bg-white rounded-md border border-gray-300 shadow-lg dark:bg-gray-800 dark:border-gray-600">
        <div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-200">
          No {{ itemLabel }} available
        </div>
      </div>
    </div>

    <!-- Error Message
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p> -->

    <!-- Help Text -->
    <p v-if="helpText" class="mt-1 text-sm text-gray-500 dark:text-gray-200">
      {{ helpText }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

// Generic item interface - can be extended for specific use cases
interface GenericItem {
  [key: string]: any
}


interface Props {
  // Input properties
  modelValue?: string | number
  inputId?: string
  inputType?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  value?: string | number

  // Label and help text
  label?: string
  helpText?: string
  itemLabel?: string // e.g., "banks", "products", "users"

  // Styling
  inputClasses?: string
  size?: 'sm' | 'md' | 'lg'

  // Data configuration
  items?: GenericItem[]
  loading?: boolean

  // Custom display functions
  displayKey?: string // Key to use for display text
  subtextKey?: string // Key to use for subtext
  valueKey?: string // Key to use for value (defaults to displayKey)

  // Search configuration
  searchKeys?: string[] // Keys to search through
  minSearchLength?: number // Minimum characters before searching

  // Events
  onItemSelect?: (item: GenericItem) => void
  onSearch?: (query: string) => void
  onError?: (error: string) => void

  // Custom search function
  customSearch?: (items: GenericItem[], query: string) => GenericItem[]
}

interface Emits {
  (e: 'update:modelValue', value: string | number): void
  (e: 'item-selected', item: GenericItem): void
  (e: 'search', query: string): void
  (e: 'error', error: string): void
}

const props = withDefaults(defineProps<Props>(), {
  inputId: 'searchable-dropdown',
  inputType: 'text',
  required: false,
  disabled: false,
  placeholder: 'Search...',
  inputClasses: '',
  size: 'md',
  itemLabel: 'items',
  items: () => [],
  value: '',
  loading: false,
  displayKey: 'name',
  subtextKey: '',
  valueKey: '',
  searchKeys: [],
  minSearchLength: 0
})

// watch(() => props.modelValue, (newValue) => {
//   console.log('newValue', newValue)
//   searchValue.value = props.items.find(item => item.code === newValue)?.name || ''
// })

const emit = defineEmits<Emits>()

// Reactive state
const searchValue = ref(props.modelValue?.toString() || '')
const showDropdown = ref(false)
const localItems = ref<GenericItem[]>(props.items || [])
const localLoading = ref(props.loading)

const filteredItems = computed(() => {
  if (!searchValue.value.trim() || searchValue.value.length < props.minSearchLength) {
    return localItems.value
  }

  if (props.customSearch) {
    return props.customSearch(localItems.value, searchValue.value)
  }

  const searchTerm = searchValue.value.toLowerCase()
  const keysToSearch = props.searchKeys.length > 0 ? props.searchKeys : [props.displayKey]

  return localItems.value.filter(item =>
    keysToSearch.some(key => {
      const value = item[key]
      return value && value.toString().toLowerCase().includes(searchTerm)
    })
  )
})

watch(() => props.items, (newItems) => {
  localItems.value = newItems || []
})

watch(() => props.loading, (newLoading) => {
  localLoading.value = newLoading
})

// Watch for searchValue changes and emit
watch(searchValue, (newValue) => {
  emit('update:modelValue', newValue)
})

// Methods
const getItemKey = (item: GenericItem): string | number => {
  return item.id || item.key || JSON.stringify(item)
}

const getItemDisplay = (item: GenericItem): string => {
  return item[props.displayKey] || 'Unknown'
}

const getItemSubtext = (item: GenericItem): string => {
  if (!props.subtextKey) return ''
  return item[props.subtextKey] || ''
}

const getItemValue = (item: GenericItem): string | number => {
  if (props.valueKey) {
    return item[props.valueKey]
  }
  return item[props.displayKey] || ''
}

const handleSearch = () => {
  emit('search', searchValue.value)
}

const selectItem = (item: GenericItem) => {
  const value = getItemValue(item)
  searchValue.value = value.toString()
  showDropdown.value = false

  emit('item-selected', item)

  // Call custom callback if provided
  if (props.onItemSelect) {
    props.onItemSelect(item)
  }
}

const handleBlur = () => {
  // Delay hiding dropdown to allow click events
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.searchable-dropdown-container')) {
    showDropdown.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Expose methods for parent components
defineExpose({
  selectItem,
  clearSearch: () => {
    searchValue.value = ''
  },
  getSelectedItem: () => {
    if (!searchValue.value) return null
    return localItems.value.find(item => getItemValue(item).toString() === searchValue.value)
  },
  setItems: (items: GenericItem[]) => {
    localItems.value = items
  },
  setLoading: (loading: boolean) => {
    localLoading.value = loading
  }
})
</script>

<style scoped>
.searchable-dropdown-container {
  position: relative;
}
</style>
